// @flow

import { completeRequest, beginRequest } from "@store/utils";
import type { StandardQuestionsState } from "./types";
import type { Action } from "redux-action";
import type { ApprovableContainer } from "@modules/service/types";
import type { StandardQuestion } from "@modules/service/reducer/standard-questions/types";

export type ShowStandardQuestionModalRequest = $Exact<{
  show: boolean,
  step?: number,
}>;

const startRequest = beginRequest(["requestsInProgress"]);

const finishRequest = completeRequest(["requestsInProgress"]);

export const onAddNewStandardQuestion = (state: StandardQuestionsState) => () =>
  startRequest(state);
export const onAddNewStandardQuestionSuccess = (state: StandardQuestionsState) => () =>
  finishRequest(state);
export const onAddNewStandardQuestionError = (state: StandardQuestionsState) => () =>
  finishRequest(state);

export const onUpdateStandardQuestion = (state: StandardQuestionsState) => () =>
  startRequest(state);
export const onUpdateStandardQuestionSuccess = (state: StandardQuestionsState) => () =>
  finishRequest(state);
export const onUpdateStandardQuestionError = (state: StandardQuestionsState) => () =>
  finishRequest(state);

export const onGetStandardQuestions = (state: StandardQuestionsState) => () => startRequest(state);

export const onGetStandardQuestionsSuccess = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  list: payload,
});

export const onGetStandardQuestionsError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onNewStandardQuestion = (state: StandardQuestionsState) => () => ({
  ...state,
  draft: {
    standardQuestion: {},
    advisorType: null,
    isActive: true,
  },
});

export const onSetShowAddStandardQuestionModal = (state: StandardQuestionsState) => ({
  payload,
}: Action<ShowStandardQuestionModalRequest>) => {
  if (!payload) {
    return state;
  }

  return {
    ...state,
    modals: {
      ...state.modals,
      showAddStandardQuestionModal: payload.show,
      modalStep: payload.step || state.modals.modalStep,
    },
  };
};

export const onUpdateStandardQuestionDraft = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  draft: payload,
});

export const onSetStandardQuestionRevisionSchema = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  error: null,
  schema: payload,
});

export const onSetStandardQuestionAssignments = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  assignments: payload,
});

export const onSetStandardQuestionDraftAssignments = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  draftAssignments: payload,
});

export const onGetStandardQuestion = (state: StandardQuestionsState) => ({ payload }: Object) =>
  startRequest(state);

export const onGetStandardQuestionSuccess = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  current: payload,
});

export const onGetStandardQuestionError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onShowStandardQuestionEditModal = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showEditModal: payload,
  },
});

export const onStartStandardQuestionDraftSuccess = (state: StandardQuestionsState) => (
  action: Action<ApprovableContainer<StandardQuestion>>,
) => ({
  ...state,
  draft: (action.payload && action.payload.active) || {},
});

export const onStartStandardQuestionDraftError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onUpdateStandardQuestionAssignmentsError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onShowEditStandardQuestionAssignmentsModal = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showEditAssignmentsModal: payload,
  },
});
